"use client";

// Chat BeBot du player Reps, copie du design Brilliant :
// - DESKTOP : le chat déplie sa largeur (ease-out) dans la colonne gauche et
//   POUSSE la carte de la leçon, qui se compresse ; messages en bulles.
// - MOBILE : bottom sheet qui monte du bas (poignée de glisse, coins arrondis,
//   fond gris clair, BeBot posé sur le bord), la LEÇON RESTE VISIBLE au-dessus
//   (pas de fond assombri). Réponse en texte nu, question en bulle grise à
//   droite. Glisse vers le bas pour fermer.
// - Messages ancrés EN BAS, les anciens s'estompent ; révélation lettre par
//   lettre (même animation que le chat des challenges).
// - Un seul bouton (chat), pas de micro pour le moment.
// LE CHAT EST MUET, VOLONTAIREMENT : BeBot ne parle que pour le CONTENU des
// leçons (écrans de concept, verdicts, indices du player). Une conversation est
// du texte libre, jamais deux fois le même : chaque réponse serait une synthèse
// ElevenLabs payée plein tarif, par personne, sans jamais toucher le cache R2.
// Ne pas rebrancher de synthèse ici.
// Accès par palier (REPS_ASSISTANT_TIER dans lib/reps/access.js) : le paywall
// suit automatiquement, et la route API re-vérifie côté serveur.

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import { motion, AnimatePresence, useDragControls } from "motion/react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import StreamingText from "@/components/ui/StreamingText/StreamingText";
import { renderRich } from "@/components/Reps/RepsExercise";
import { hasRepsAssistant, REPS_ASSISTANT_TIER } from "@/lib/reps/access";

const BEBOT = "/images/bebot/coach/happy.webp";
const BEBOT_SAD = "/images/bebot/coach/sad.webp";
const PANEL_W = 340;

// Carte d'upsell (accès verrouillé) partagée desktop/mobile.
function UpsellCard({ className }) {
    const t = useTranslations();
    return (
        <div
            className={`pointer-events-auto w-72 rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-[#253340] ${className || ""}`}
        >
            <p className="mb-1 text-sm font-bold text-gray-900 dark:text-white">
                {t("reps_assistant_locked_title")}
            </p>
            <p className="mb-3 text-[13px] leading-relaxed text-gray-500 dark:text-grayDark-300">
                {REPS_ASSISTANT_TIER === "plus"
                    ? t("reps_assistant_locked_body_plus")
                    : t("reps_assistant_locked_body_premium")}
            </p>
            <Link
                href="/pricing"
                className="block w-full rounded-xl bg-primary py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-primary/90 hover:text-white"
            >
                {t("reps_assistant_locked_cta")}
            </Link>
        </div>
    );
}

// ------- Dock BeBot PERSISTANT (desktop) -------
// Façon Brilliant : la mascotte est montée UNE fois et ne se démonte JAMAIS ->
// jamais de disparition/réapparition, la MÊME animation Lottie tourne en continu.
// Elle reste posée en bas à gauche ; à sa droite, la pilule chat quand c'est
// fermé, l'input du panneau quand c'est ouvert. Clic sur BeBot = ouvrir/fermer.
// `hidden` masque en douceur (opacité) sans démonter -> la Lottie ne repart pas
// de zéro (écrans de fin / interstitiels).
// `mood` : c'est BeBot qui porte le verdict de la leçon (façon Brilliant) —
// "sad" le fait passer en pose triste le temps d'une mauvaise réponse.
export function RepsAssistantDock({ open, onToggle, hidden, mood }) {
    const t = useTranslations();
    const { data: session } = useSession();
    const allowed = hasRepsAssistant(session && session.user);
    const [showUpsell, setShowUpsell] = useState(false);

    function handleClick() {
        if (!allowed) {
            setShowUpsell((v) => !v);
            return;
        }
        onToggle();
    }

    return (
        <div
            className="pointer-events-none fixed bottom-5 left-5 z-[75] hidden sm:block"
            style={{
                opacity: hidden ? 0 : 1,
                transition: "opacity 0.2s ease-out",
            }}
            aria-hidden={hidden ? true : undefined}
        >
            <AnimatePresence>
                {showUpsell && !open && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                        <UpsellCard className="mb-3" />
                    </motion.div>
                )}
            </AnimatePresence>
            <div
                className="flex items-end gap-2"
                style={{ pointerEvents: hidden ? "none" : "auto" }}
            >
                {/* BeBot PERSISTANT : jamais démonté (ancienne icône statique,
                    préférée à la Lottie). */}
                <button
                    type="button"
                    onClick={handleClick}
                    tabIndex={hidden ? -1 : undefined}
                    aria-label={
                        open
                            ? t("reps_assistant_close_aria")
                            : t("reps_assistant_open_aria")
                    }
                    title={
                        open
                            ? t("reps_assistant_close_aria")
                            : t("reps_assistant_open_aria")
                    }
                    className="relative block transition-transform hover:scale-105 active:scale-95"
                >
                    <img
                        src={mood === "sad" ? BEBOT_SAD : BEBOT}
                        alt="BeBot"
                        className="pointer-events-none h-16 w-16 object-contain"
                    />
                    {!allowed && (
                        <span className="absolute -right-0.5 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-400 text-white dark:bg-grayDark-600">
                            <SolarIcon
                                name="lock-keyhole-minimalistic-bold"
                                className="text-[9px]"
                            />
                        </span>
                    )}
                </button>
                {/* Pilule chat : seulement quand c'est fermé (l'input du panneau
                    prend le relais à droite du BeBot quand c'est ouvert). */}
                <AnimatePresence>
                    {!open && (
                        <motion.div
                            key="dock-pill"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.16, ease: "easeOut" }}
                            className="mb-1"
                        >
                            <button
                                type="button"
                                onClick={handleClick}
                                tabIndex={hidden ? -1 : undefined}
                                aria-label={t("reps_assistant_open_aria")}
                                title={t("reps_assistant_open_aria")}
                                className="relative flex h-11 w-14 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-600 dark:border-white/10 dark:bg-white/10 dark:text-grayDark-400 dark:hover:bg-white/15 dark:hover:text-grayDark-200"
                            >
                                <SolarIcon
                                    name="chat-round-line-linear"
                                    className="text-xl"
                                />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// ------- Launcher MOBILE (bouton dans la barre d'action) -------
// Sur desktop, c'est RepsAssistantDock qui gère le BeBot. Sur MOBILE, le chat
// s'ouvre depuis un bouton COMPACT intégré à la barre d'action du bas (élément de
// mise en page) : fini la mascotte flottante qui passait par-dessus le texte qui
// défile derrière elle. `sm:hidden` -> invisible sur desktop.
export function RepsAssistantLauncher({ onOpen }) {
    const t = useTranslations();
    const { data: session } = useSession();
    const allowed = hasRepsAssistant(session && session.user);
    const [showUpsell, setShowUpsell] = useState(false);

    function handleOpen() {
        if (!allowed) {
            setShowUpsell((v) => !v);
            return;
        }
        onOpen();
    }

    return (
        <div className="relative shrink-0 sm:hidden">
            <AnimatePresence>
                {showUpsell && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        // Le lanceur vit désormais dans la barre du HAUT : la
                        // carte se déplie donc vers le bas, alignée à droite
                        // pour ne pas sortir de l'écran.
                        className="absolute right-0 top-full z-[70] mt-3"
                    >
                        <UpsellCard />
                    </motion.div>
                )}
            </AnimatePresence>
            <button
                type="button"
                onClick={handleOpen}
                aria-label={t("reps_assistant_open_aria")}
                title={t("reps_assistant_open_aria")}
                // Barre du haut : même langage que le son et le signalement
                // (icône au trait, gris clair, fond au survol seulement).
                className="relative flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
            >
                <SolarIcon name="chat-round-line-linear" className="text-xl" />
                {!allowed && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-400 text-white ring-2 ring-white dark:bg-grayDark-600 dark:ring-[#22303C]">
                        <SolarIcon
                            name="lock-keyhole-minimalistic-bold"
                            className="text-[9px]"
                        />
                    </span>
                )}
            </button>
        </div>
    );
}

// ------- Panneau : conversation ancrée en bas + saisie -------
// MONTÉ EN PERMANENCE (ne se démonte pas à la fermeture -> conversation gardée) ;
// `open` pilote seulement la visibilité (largeur desktop / translation mobile).
// Expose une petite API impérative (ref) pour le player : l'arbitrage d'une
// réponse contestée se vit DANS le chat (bulle « BeBot écrit » puis réponse).
export const RepsAssistantPanel = forwardRef(function RepsAssistantPanel(
    { open, onClose, getContext },
    ref
) {
    const t = useTranslations();
    const locale = useLocale();
    // Mobile : panneau plein écran (pas de room pour le côte-à-côte). Le panneau
    // monte à l'ouverture (client) -> matchMedia dispo dès l'initialiseur.
    const [isMobile, setIsMobile] = useState(
        () =>
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 639px)").matches
    );
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 639px)");
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    const dragControls = useDragControls();
    // Mobile : hauteur de la sheet réglable (2 crans). Glisse vers le haut pour
    // agrandir, vers le bas pour réduire (puis fermer si déjà réduite).
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    // Le message d'accueil se révèle lettre par lettre lui aussi : posé avec
    // streaming=true (l'animation démarre) puis repassé à false au tick suivant
    // (StreamingText continue la révélation à son rythme jusqu'au bout).
    const [welcomeStreaming, setWelcomeStreaming] = useState(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    // Init une seule fois, à la PREMIÈRE ouverture (le panneau étant monté en
    // permanence, on n'initialise pas tant que l'utilisateur ne l'a pas ouvert).
    const initedRef = useRef(false);
    // Révélation lettre à lettre d'un message INJECTÉ par le player (arbitrage).
    const [injectedStreaming, setInjectedStreaming] = useState(false);
    // Arbitrage en attente : on FIGE l'envoi (input désactivé) le temps que la
    // bulle « BeBot écrit » se remplisse — sinon le flux de send() et
    // l'arbitrage se disputeraient les bulles.
    const [appealPending, setAppealPending] = useState(false);
    // Identité des placeholders de send() : chaque bulle streamée porte un id
    // et le flux remplit LA SIENNE, jamais « la dernière » (une bulle
    // d'arbitrage peut s'intercaler pendant un stream encore en vol).
    const streamSeqRef = useRef(0);

    // API impérative pour le player : l'arbitrage d'une réponse contestée
    // s'affiche dans le chat. beginAssistantMessage() pose la bulle « BeBot
    // écrit » (pointillés, marquée appeal) ; completeAssistantMessage(text) la
    // remplit — ELLE, où qu'elle soit — avec la révélation lettre à lettre.
    // Si le chat n'a jamais été ouvert, on saute l'accueil : l'arbitrage EST
    // la première prise de parole de BeBot.
    useImperativeHandle(ref, () => ({
        beginAssistantMessage() {
            initedRef.current = true;
            setAppealPending(true);
            setMessages((m) => {
                const last = m[m.length - 1];
                // Une seule bulle d'arbitrage en attente à la fois.
                if (last && last.appeal && !last.content) return m;
                return [
                    ...m,
                    // L'appui sur « Je pense que ma réponse est juste » est une
                    // prise de parole de l'APPRENANT : on la matérialise en
                    // bulle user. L'historique alterne alors proprement
                    // (user → verdict → suite) et le serveur ne jette plus un
                    // verdict orphelin (règle « premier message = user »).
                    { role: "user", content: t("reps_appeal_button") },
                    { role: "assistant", content: "", appeal: true },
                ];
            });
        },
        completeAssistantMessage(text) {
            setAppealPending(false);
            setMessages((m) => {
                const next = [...m];
                for (let i = next.length - 1; i >= 0; i--) {
                    if (next[i].appeal && !next[i].content) {
                        next[i] = { ...next[i], content: text };
                        return next;
                    }
                }
                next.push({ role: "assistant", content: text, appeal: true });
                return next;
            });
            // Même mécanique que l'accueil : streaming éphémère, StreamingText
            // poursuit la révélation à son rythme jusqu'au bout.
            setInjectedStreaming(true);
            setTimeout(() => setInjectedStreaming(false), 60);
        },
    }));

    useEffect(() => {
        if (!open) return undefined;

        // Focus le champ à chaque ouverture, une fois l'animation passée.
        const focusId = setTimeout(
            () => inputRef.current?.focus(),
            isMobile ? 420 : 380
        );

        if (initedRef.current) {
            return () => clearTimeout(focusId);
        }
        initedRef.current = true;

        // Message d'accueil, révélé lettre par lettre. Affiché TOUT DE SUITE :
        // le chat étant muet, plus rien ne justifie de le retenir.
        setMessages([
            { role: "assistant", content: t("reps_assistant_welcome") },
        ]);
        setWelcomeStreaming(true);
        const streamId = setTimeout(() => setWelcomeStreaming(false), 60);

        return () => {
            clearTimeout(focusId);
            clearTimeout(streamId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // À la fermeture, on repasse la sheet mobile en hauteur par défaut : la
    // conversation reste (state persistant), mais la réouverture est compacte.
    useEffect(() => {
        if (!open) setExpanded(false);
    }, [open]);

    const scrollToBottom = useCallback(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    async function send() {
        const text = input.trim();
        if (!text || busy || appealPending) return;
        setInput("");
        setBusy(true);
        const history = messages;
        // Le placeholder du stream porte un id : les deltas rempliront CETTE
        // bulle même si une bulle d'arbitrage s'intercale entre-temps.
        const mid = ++streamSeqRef.current;
        setMessages((m) => [
            ...m,
            { role: "user", content: text },
            { role: "assistant", content: "", mid },
        ]);

        try {
            let full = "";
            const streamOnce = async () => {
                const res = await fetch("/api/reps/assistant", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        message: text,
                        history,
                        // BeBot répond dans la langue de l'interface (comme la voix).
                        locale,
                        context: getContext ? getContext() : undefined,
                    }),
                });
                // Garde-fou d'usage (plafond ou blocage admin) : le serveur envoie
                // un message poli — on l'affiche tel quel dans la bulle, jamais
                // l'erreur générique qui inviterait à réessayer en boucle.
                if (res.status === 429) {
                    const data = await res.json().catch(() => null);
                    if (data?.message) {
                        const politeError = new Error(data.message);
                        politeError.politeMessage = data.message;
                        throw politeError;
                    }
                }
                if (!res.ok || !res.body) throw new Error("bad response");

                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";
                    for (const line of lines) {
                        if (!line.startsWith("data: ")) continue;
                        let evt;
                        try {
                            evt = JSON.parse(line.slice(6));
                        } catch (e) {
                            continue;
                        }
                        if (evt.type === "text") {
                            full += evt.content;
                            setMessages((m) =>
                                m.map((msg) =>
                                    msg.mid === mid
                                        ? {
                                              ...msg,
                                              content: msg.content + evt.content,
                                          }
                                        : msg
                                )
                            );
                        }
                        if (evt.type === "error") throw new Error(evt.message);
                    }
                }
            };
            // Une surcharge amont fait échouer la requête AVANT le premier
            // caractère (« Je n'ai pas réussi à répondre » alors que rien n'a
            // même été tenté deux fois). Réessayer coûte une seconde ; brûler
            // le message de l'apprenant coûte sa question. Deux reprises,
            // JAMAIS si un début de réponse est arrivé (on dupliquerait), et
            // jamais sur un message poli du serveur (plafond, blocage).
            for (let tentative = 0; ; tentative++) {
                try {
                    await streamOnce();
                    break;
                } catch (e) {
                    if (e?.politeMessage || full || tentative >= 2) throw e;
                    await new Promise((r) => setTimeout(r, 900 * (tentative + 1)));
                }
            }
        } catch (e) {
            setMessages((m) =>
                m.map((msg) =>
                    msg.mid === mid
                        ? {
                              ...msg,
                              // Plafond d'usage : le message poli du serveur
                              // (« BeBot reprend son souffle ») plutôt que
                              // l'erreur générique.
                              content:
                                  msg.content ||
                                  e?.politeMessage ||
                                  t("reps_assistant_error"),
                          }
                        : msg
                )
            );
        } finally {
            setBusy(false);
        }
    }

    // Barre d'envoi façon Brilliant : pilule arrondie, flèche d'envoi à droite.
    function inputField() {
        const hasText = input.trim().length > 0;
        return (
            <div className="flex flex-1 items-center rounded-full border border-gray-200 bg-white pl-5 pr-1.5 shadow-sm transition-colors focus-within:border-gray-300 dark:border-white/10 dark:bg-[#253340] dark:focus-within:border-white/25">
                <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                            e.preventDefault();
                            send();
                        }
                        if (e.key === "Escape") onClose();
                    }}
                    placeholder={t("reps_assistant_placeholder")}
                    disabled={busy || appealPending}
                    autoComplete="off"
                    spellCheck={false}
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-hidden placeholder:text-gray-400 dark:text-white dark:placeholder:text-grayDark-500"
                />
                <button
                    type="button"
                    onClick={send}
                    aria-label={t("reps_assistant_send_aria")}
                    disabled={!hasText || busy || appealPending}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all ${
                        hasText && !busy && !appealPending
                            ? "bg-primary text-white hover:bg-primary/90"
                            : "text-gray-300 dark:text-grayDark-600"
                    }`}
                >
                    <SolarIcon name="plain-2-bold" className="text-base" />
                </button>
            </div>
        );
    }

    // Liste des messages. variant "sheet" (mobile façon Brilliant : réponse en
    // texte nu, question dans une bulle grise à droite) ou "panel" (desktop :
    // bulles bordées bleu pâle / blanc). Anciens messages estompés dans les deux.
    function renderMessages(variant) {
        return messages.map((m, i) => {
            const faded = i < messages.length - 2;
            const isStreaming =
                m.role === "assistant" &&
                ((busy && i === messages.length - 1) ||
                    (i === 0 && welcomeStreaming) ||
                    (i === messages.length - 1 && injectedStreaming));
            const content = m.content ? (
                m.role === "assistant" ? (
                    <StreamingText
                        text={m.content}
                        streaming={isStreaming}
                        onReveal={scrollToBottom}
                        renderFinal={(txt) => <>{renderRich(txt)}</>}
                    />
                ) : (
                    m.content
                )
            ) : (
                <span className="inline-flex gap-1 py-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:120ms]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:240ms]" />
                </span>
            );

            let cls;
            if (variant === "sheet") {
                cls =
                    m.role === "user"
                        ? `self-end max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-[15px] leading-relaxed ${
                              faded
                                  ? "bg-gray-200/50 text-gray-400 dark:bg-white/[0.04] dark:text-grayDark-500"
                                  : "bg-gray-200/90 text-gray-700 dark:bg-white/10 dark:text-grayDark-100"
                          }`
                        : `max-w-full whitespace-pre-wrap text-[15px] leading-relaxed ${
                              faded
                                  ? "text-gray-400 dark:text-grayDark-500"
                                  : "text-gray-800 dark:text-grayDark-100"
                          }`;
            } else {
                cls =
                    m.role === "user"
                        ? `self-end max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-br-md border px-4 py-2.5 text-[13.5px] leading-relaxed ${
                              faded
                                  ? "border-gray-100 bg-white text-gray-400 dark:border-white/[0.06] dark:bg-white/[0.03] dark:text-grayDark-500"
                                  : "border-gray-200 bg-white text-gray-700 dark:border-white/10 dark:bg-white/[0.06] dark:text-grayDark-100"
                          }`
                        : `self-start max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-bl-md border px-4 py-2.5 text-[13.5px] leading-relaxed ${
                              faded
                                  ? "border-gray-100 bg-white text-gray-400 dark:border-white/[0.06] dark:bg-white/[0.03] dark:text-grayDark-500"
                                  : "border-blue-100 bg-blue-50/80 text-gray-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-grayDark-100"
                          }`;
            }

            return (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className={cls}
                >
                    {content}
                </motion.div>
            );
        });
    }

    // ---------------- MOBILE : bottom sheet façon Brilliant ----------------
    if (isMobile) {
        return (
            <motion.div
                key="reps-chat-sheet"
                initial={{ y: "100%" }}
                animate={{ y: open ? 0 : "100%" }}
                transition={{ type: "spring", damping: 34, stiffness: 360, mass: 0.85 }}
                drag="y"
                dragListener={false}
                dragControls={dragControls}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0.16, bottom: 0.5 }}
                dragSnapToOrigin
                onDragEnd={(e, info) => {
                    const dy = info.offset.y;
                    const vy = info.velocity.y;
                    // Glisse vers le HAUT (ou lancée rapide) -> agrandir.
                    if (dy < -40 || vy < -520) {
                        setExpanded(true);
                        return;
                    }
                    // Glisse vers le BAS -> réduire si agrandie, sinon fermer.
                    if (dy > 120 || vy > 650) {
                        if (expanded) setExpanded(false);
                        else onClose();
                    }
                    // Sinon : dragSnapToOrigin ramène la sheet à sa place.
                }}
                className="fixed inset-x-0 bottom-0 z-[80]"
                style={{ pointerEvents: open ? "auto" : "none" }}
                data-bebot-chat
            >
                {/* BeBot posé sur le bord haut-gauche de la sheet (peek). Masqué
                    quand la sheet est fermée : sinon il déborde au-dessus du bord
                    et, la sheet fermée étant translatée tout en bas, il réapparaît
                    en bas de l'écran -> impression de DEUX BeBot (avec le launcher). */}
                <motion.img
                    src={BEBOT}
                    alt="BeBot"
                    animate={{ opacity: open ? 1 : 0 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="pointer-events-none absolute -top-7 left-4 z-10 h-14 w-14 drop-shadow-md object-contain"
                />
                <div
                    className="flex flex-col rounded-t-[26px] bg-[#f4f4f7] shadow-[0_-10px_40px_rgba(0,0,0,0.14)] dark:bg-[#22303C]"
                    style={{
                        height: expanded ? "92dvh" : "60dvh",
                        transition: "height 0.32s cubic-bezier(0.22,1,0.36,1)",
                    }}
                >
                    {/* Poignée de glisse : seule zone qui déclenche le drag (les
                        messages scrollent librement). Glisse vers le haut pour
                        agrandir, vers le bas pour réduire puis fermer. */}
                    <div
                        onPointerDown={(e) => dragControls.start(e)}
                        className="flex shrink-0 cursor-grab touch-none justify-center pb-1 pt-2.5 active:cursor-grabbing"
                    >
                        <span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-white/20" />
                    </div>

                    {/* Messages : ancrés en bas, réponse en texte nu (Brilliant). */}
                    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                        <div className="flex min-h-full flex-col justify-end gap-3 px-5 pb-3 pt-8">
                            {renderMessages("sheet")}
                        </div>
                    </div>

                    {/* Saisie : pilule blanche sur la sheet grise. */}
                    <div
                        className="px-4 pt-2"
                        style={{
                            paddingBottom:
                                "max(1rem, env(safe-area-inset-bottom))",
                        }}
                    >
                        {inputField()}
                    </div>
                </div>
            </motion.div>
        );
    }

    // ---------------- DESKTOP : panneau latéral qui pousse la carte ----------------
    return (
        <motion.div
            key="reps-chat-panel"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: open ? PANEL_W : 0, opacity: open ? 1 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[70] hidden shrink-0 overflow-hidden sm:block"
            style={{ pointerEvents: open ? "auto" : "none" }}
            data-bebot-chat
        >
            {/* Largeur interne FIXE : le texte ne se recompose pas au dépliage. */}
            <div className="flex h-full flex-col" style={{ width: PANEL_W }}>
                <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                    <div className="flex min-h-full flex-col justify-end gap-2.5 px-5 pb-3 pt-4">
                        {renderMessages("panel")}
                    </div>
                </div>
                {/* Pas de BeBot ici : c'est le BeBot PERSISTANT (RepsAssistantDock,
                    64px) qui reste posé en bas à gauche, PAR-DESSUS ce coin. On
                    décale donc l'input vers la droite (pl) pour lui laisser sa place. */}
                <div className="flex items-end gap-2 px-4 pb-5 pl-24">
                    {inputField()}
                </div>
            </div>
        </motion.div>
    );
});

// Poignée entre le chat et la carte (détail Brilliant) : un petit trait vertical
// cliquable qui replie le panneau.
export function RepsAssistantHandle({ onClose }) {
    const t = useTranslations();
    return (
        <motion.button
            type="button"
            onClick={onClose}
            aria-label={t("reps_assistant_close_aria")}
            title={t("reps_assistant_close_aria")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="group z-[70] hidden w-3 shrink-0 items-center justify-center sm:flex"
        >
            <span className="h-10 w-1 rounded-full bg-gray-300 transition-colors group-hover:bg-gray-400 dark:bg-white/20 dark:group-hover:bg-white/35" />
        </motion.button>
    );
}
